import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  BookOpen,
  Building2,
  CheckCircle2,
  FileText,
  LockKeyhole,
  Menu,
  Network,
  ShieldCheck,
  X,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const setupSteps = [
  {
    title: 'Tenant shell',
    description: 'Create the workspace and first tenant admin without issuing employee codes.',
  },
  {
    title: 'Company setup',
    description: 'Choose single company or group, then define legal entities and prefixes.',
  },
  {
    title: 'Employee master',
    description:
      'Create employees only after departments, locations, positions, and headcount rules exist.',
  },
  {
    title: 'Documents',
    description: 'Generate joining kits and controlled employee documents from managed templates.',
  },
];

const productChecks = [
  'Legal entity and employee-code rules',
  'Departments, designations, locations, positions',
  'MFA, passkeys, sessions, and auth policy',
  'Document templates, uploads, and audit trails',
];

const platformCapabilities = [
  {
    icon: Building2,
    title: 'Company foundation first',
    description:
      'Model tenant, group company, legal entity, location, department, designation, and position before hiring.',
  },
  {
    icon: Network,
    title: 'Position-aware employee records',
    description:
      'Headcount, location, legal-entity, reporting, and assignment rules are enforced on the backend.',
  },
  {
    icon: FileText,
    title: 'Document-backed onboarding',
    description:
      'Education, experience, employee documents, joining-kit PDFs, and signature tasks stay attached to the employee profile.',
  },
  {
    icon: LockKeyhole,
    title: 'Modern access security',
    description:
      'Tenant policies cover password rules, MFA, passkey auditing, sessions, verification, sign-in recovery, and identity-scoped notifications.',
  },
];

function PublicHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const links = [
    { label: 'Product', href: '#product' },
    { label: 'Security', href: '#security' },
    { label: 'Docs', to: '/docs' },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" aria-label="UniTree home">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950 text-sm font-semibold text-white">
            UT
          </span>
          <span className="text-lg font-semibold tracking-normal text-slate-950">UniTree</span>
        </Link>

        <nav className="hidden items-center gap-8 text-sm font-medium text-slate-600 md:flex">
          {links.map((link) =>
            'to' in link ? (
              <Link key={link.label} to={link.to} className="hover:text-slate-950">
                {link.label}
              </Link>
            ) : (
              <a key={link.label} href={link.href} className="hover:text-slate-950">
                {link.label}
              </a>
            ),
          )}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <Link to="/login">
            <Button variant="ghost" className="text-slate-700 hover:text-slate-950">
              Sign in
            </Button>
          </Link>
          <Link to="/signup">
            <Button className="bg-emerald-700 text-white hover:bg-emerald-800">Start setup</Button>
          </Link>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setIsMenuOpen((value) => !value)}
          aria-label="Toggle navigation"
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      <div className={cn('border-t border-slate-200 md:hidden', isMenuOpen ? 'block' : 'hidden')}>
        <div className="space-y-1 px-4 py-4">
          {links.map((link) =>
            'to' in link ? (
              <Link key={link.label} to={link.to} className="block rounded-md px-3 py-2 text-sm">
                {link.label}
              </Link>
            ) : (
              <a key={link.label} href={link.href} className="block rounded-md px-3 py-2 text-sm">
                {link.label}
              </a>
            ),
          )}
          <div className="grid gap-2 px-3 pt-3">
            <Link to="/login">
              <Button variant="outline" className="w-full">
                Sign in
              </Button>
            </Link>
            <Link to="/signup">
              <Button className="w-full bg-emerald-700 text-white hover:bg-emerald-800">
                Start setup
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}

function SetupWorkspacePreview() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <div>
          <p className="text-sm font-semibold text-slate-950">Tenant setup checklist</p>
          <p className="text-xs text-slate-500">Admin identity active, HRMS foundation pending</p>
        </div>
        <span className="rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
          Guided
        </span>
      </div>
      <div className="grid gap-2.5 p-4">
        {productChecks.map((check, index) => (
          <div
            key={check}
            className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5"
          >
            <span
              className={cn(
                'mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                index === 0 ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-500',
              )}
            >
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-slate-950">{check}</p>
              <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                <div
                  className={cn(
                    'h-1.5 rounded-full',
                    index === 0 ? 'w-2/3 bg-emerald-700' : 'w-1/4 bg-slate-300',
                  )}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white text-slate-950">
      <PublicHeader />

      <main>
        <section className="border-b border-slate-200">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-10 sm:px-6 lg:grid-cols-[0.92fr_1.08fr] lg:px-8 lg:py-8">
            <div className="max-w-2xl">
              <h1 className="text-5xl font-semibold leading-[1.02] tracking-normal text-slate-950 sm:text-6xl lg:text-7xl">
                UniTree
              </h1>
              <p className="mt-5 text-lg leading-8 text-slate-600 sm:text-xl">
                Enterprise HRMS for companies that need tenant setup, legal entities, employee-code
                governance, documents, MFA, passkeys, and ABAC to work before the first employee is
                hired.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Link to="/signup">
                  <Button size="lg" className="bg-emerald-700 text-white hover:bg-emerald-800">
                    Start setup
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/docs">
                  <Button size="lg" variant="outline" className="border-slate-300">
                    Read public docs
                    <BookOpen className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>

              <div className="mt-8 grid gap-3 text-sm text-slate-600 sm:grid-cols-2">
                {[
                  'Admin identity is not an employee by default',
                  'Employee codes start after company setup',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid gap-5">
              <img
                src="/marketing/unitree-public-concept.png"
                alt="UniTree public product concept"
                className="aspect-[16/9] w-full rounded-xl border border-slate-200 object-cover object-top shadow-sm lg:aspect-[16/7]"
              />
              <SetupWorkspacePreview />
            </div>
          </div>
        </section>

        <section id="product" className="border-b border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <h2 className="text-3xl font-semibold tracking-normal text-slate-950 sm:text-4xl">
                Setup follows the same order your HR operations need.
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                UniTree separates tenant administration from employment records. First admins can
                configure the tenant without consuming employee serials or creating inaccurate HR
                master data.
              </p>
            </div>

            <div className="mt-10 grid gap-4 md:grid-cols-4">
              {setupSteps.map((step, index) => (
                <div key={step.title} className="rounded-lg border border-slate-200 bg-white p-5">
                  <div className="mb-5 flex h-9 w-9 items-center justify-center rounded-lg bg-slate-950 text-sm font-semibold text-white">
                    {index + 1}
                  </div>
                  <h3 className="text-base font-semibold text-slate-950">{step.title}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="security" className="bg-white">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[0.85fr_1.15fr] lg:px-8">
            <div>
              <ShieldCheck className="h-9 w-9 text-emerald-700" />
              <h2 className="mt-5 text-3xl font-semibold tracking-normal text-slate-950">
                Built for governed HR and tenant administration.
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600">
                Security, identity, and HR records are separate layers. Admin accounts configure the
                workspace; employee records represent actual employment relationships. Platform
                administrators must complete MFA before a session is issued.
              </p>
              <div className="mt-8 flex gap-3">
                <Link to="/docs">
                  <Button variant="outline">Open docs</Button>
                </Link>
                <Link to="/login">
                  <Button className="bg-slate-950 text-white hover:bg-slate-800">Sign in</Button>
                </Link>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {platformCapabilities.map((capability) => {
                const Icon = capability.icon;
                return (
                  <div
                    key={capability.title}
                    className="rounded-lg border border-slate-200 bg-white p-5"
                  >
                    <Icon className="h-6 w-6 text-emerald-700" />
                    <h3 className="mt-4 text-base font-semibold text-slate-950">
                      {capability.title}
                    </h3>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      {capability.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 md:flex-row md:items-center md:justify-between lg:px-8">
          <div>
            <p className="text-sm font-semibold">UniTree</p>
            <p className="mt-1 text-sm text-slate-400">
              Tenant setup first. Employee records when ready.
            </p>
          </div>
          <div className="flex flex-wrap gap-5 text-sm text-slate-300">
            <Link to="/docs" className="hover:text-white">
              Docs
            </Link>
            <Link to="/signup" className="hover:text-white">
              Start setup
            </Link>
            <Link to="/login" className="hover:text-white">
              Sign in
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
